import React, { useState } from 'react';
import { ChevronRight, ChevronDown, FileText, Settings } from 'lucide-react';

/**
 * MilestoneTreeRow Component
 * Displays a single milestone row in tree structure with expand/collapse
 * 
 * Props:
 * - milestone: Milestone template object
 * - level: Nesting level (0, 1, 2, etc)
 * - onConfigureEvidence: Callback when configure evidence clicked
 * - onView: Callback when view clicked
 * - onDelete: Callback when delete clicked (optional)
 */
const MilestoneTreeRow = ({ 
  milestone, 
  level = 0, 
  onConfigureEvidence,
  onView,
  onDelete 
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Calculate indentation based on level
  const indentClass = level === 0 ? '' : level === 1 ? 'ml-8' : 'ml-16';
  
  // Determine row background color based on level
  const bgColorClass = level === 0 
    ? 'bg-white' 
    : level === 1 
      ? 'bg-gray-50' 
      : 'bg-blue-50';
  
  // Toggle expand/collapse
  const toggleExpand = () => {
    if (milestone.isGroup && milestone.children?.length > 0) {
      setIsExpanded(!isExpanded);
    }
  };
  
  // Get evidence count
  const evidenceCount = milestone.evidenceDefinitions?.length || 0;
  
  // Format bobot display
  const bobotDisplay = milestone.defaultBobot 
    ? `${milestone.defaultBobot}%` 
    : '-';
  
  // Get calculation method display
  const calcMethodDisplay = milestone.calculationMethod 
    ? milestone.calculationMethod.replace('_', ' ')
    : '-';

  return (
    <>
      {/* Current Row */}
      <tr className={`border-b ${bgColorClass} hover:bg-blue-100 transition-colors`}>
        {/* NO */}
        <td className="px-4 py-3 text-sm text-gray-600 w-16">
          {milestone.displayOrder || '-'}
        </td>
        
        {/* CODE & NAME */}
        <td className={`px-4 py-3 ${indentClass}`}>
          <div className="flex items-center space-x-2">
            {/* Expand/Collapse Button */}
            {milestone.isGroup && milestone.children?.length > 0 ? (
              <button
                onClick={toggleExpand}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-600" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                )}
              </button>
            ) : (
              <div className="w-6" /> // Spacer for leaf items
            )}
            
            {/* Code Badge */}
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-mono bg-blue-100 text-blue-800 border border-blue-200">
              {milestone.itemCode}
            </span>
            
            {/* Milestone Name */}
            <div className="flex-1">
              <div className="font-medium text-gray-900">
                {milestone.milestoneName}
              </div>
              
              {/* Subtitle for leaf milestones */}
              {!milestone.isGroup && (
                <div className="text-xs text-gray-500 mt-1 space-x-2">
                  {milestone.calculationMethod && (
                    <span>
                      <span className="font-semibold">Calc:</span> {calcMethodDisplay}
                    </span>
                  )}
                  {milestone.volumeUnit && (
                    <span>
                      <span className="font-semibold">Unit:</span> {milestone.volumeUnit}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </td>
        
        {/* LEVEL */}
        <td className="px-4 py-3 text-center">
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
            level === 0 ? 'bg-purple-100 text-purple-800' :
            level === 1 ? 'bg-blue-100 text-blue-800' :
            'bg-green-100 text-green-800'
          }`}>
            L{level}
          </span>
        </td>
        
        {/* TYPE */}
        <td className="px-4 py-3 text-center">
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
            milestone.isGroup 
              ? 'bg-gray-100 text-gray-800' 
              : 'bg-green-100 text-green-800'
          }`}>
            {milestone.isGroup ? 'Group' : 'Leaf'}
          </span>
        </td>
        
        {/* BOBOT */}
        <td className="px-4 py-3 text-center">
          <span className={`text-sm font-semibold ${
            milestone.defaultBobot ? 'text-gray-900' : 'text-gray-400'
          }`}>
            {bobotDisplay}
          </span>
        </td>
        
        {/* CALC METHOD */}
        <td className="px-4 py-3 text-center text-xs text-gray-600">
          {calcMethodDisplay}
        </td>
        
        {/* EVIDENCE COUNT */}
        <td className="px-4 py-3 text-center">
          {evidenceCount > 0 ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
              📎 {evidenceCount}
            </span>
          ) : (
            <span className="text-gray-400 text-xs">-</span>
          )}
        </td>
        
        {/* ACTIONS */}
        <td className="px-4 py-3">
          <div className="flex items-center justify-center space-x-2">
            {/* View Button */}
            <button
              onClick={() => onView && onView(milestone)}
              className="p-1 hover:bg-blue-100 rounded transition-colors"
              title="View Details"
            >
              <FileText className="w-4 h-4 text-blue-600" />
            </button>
            
            {/* Configure Evidence Button - Only for leaf milestones */}
            {!milestone.isGroup && (
              <button
                onClick={() => onConfigureEvidence && onConfigureEvidence(milestone)}
                className="p-1 hover:bg-green-100 rounded transition-colors"
                title="Configure Evidence"
              >
                <Settings className="w-4 h-4 text-green-600" />
              </button>
            )}
            
            {/* Delete Button (optional) */}
            {onDelete && (
              <button
                onClick={() => onDelete(milestone)}
                className="p-1 hover:bg-red-100 rounded transition-colors"
                title="Delete"
              >
                <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </td>
      </tr>
      
      {/* Children Rows (Recursive) */}
      {isExpanded && milestone.children && milestone.children.length > 0 && (
        <>
          {milestone.children.map((child) => (
            <MilestoneTreeRow
              key={child.id}
              milestone={child}
              level={level + 1}
              onConfigureEvidence={onConfigureEvidence}
              onView={onView}
              onDelete={onDelete}
            />
          ))}
        </>
      )}
    </>
  );
};

export default MilestoneTreeRow;